import React, { useState, useCallback, useEffect } from 'react';
import DownChevron from 'src/assets/icons/DownChevron';
import CustomPopover, { useCustomPopover } from './Popover/CustomPopover';
import CheckBox from 'src/components/Form/checkbox';
import { useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

const DropDownDistributor = () => {

  const selectedProfileId = JSON.parse(sessionStorage.getItem('selectedProfileId'));

  const rawStoredValue = JSON.parse(sessionStorage.getItem('distributorId'))?.[selectedProfileId?.profileId] || null;
  
  // Get available vendor sources based on distributorView
  const getAvailableVendorSources = () => {
    const distributorView = selectedProfileId?.vendor?.distributorView;
    const sourcingExist = String(distributorView).toLowerCase().includes('sourcing');
    const manufacturingExist = String(distributorView).toLowerCase().includes('manufacturing');
    
    return [
      ...(sourcingExist ? [{ label: 'Sourcing', value: 'sourcing' }] : []),
      ...(manufacturingExist ? [{ label: 'Manufacturing', value: 'manufacturing' }] : [])
    ];
  };

  // Initialize state with proper handling of old format (single object) and new format (array)
  const [selected, setSelected] = useState(() => {
    const availableSources = getAvailableVendorSources();
    const onlySourcing = availableSources.length === 1 && availableSources[0].value === 'sourcing';

    if(onlySourcing) {
      return [{ label: 'Sourcing', value: 'sourcing' }];
    }

    if (!rawStoredValue) {
      // No value stored - default to Manufacturing
      return [{ label: 'Manufacturing', value: 'manufacturing' }];
    }

    // Normalize stored value to array format
    let storedArray = [];
    if (Array.isArray(rawStoredValue)) {
      storedArray = rawStoredValue;
    } else if (rawStoredValue && typeof rawStoredValue === 'object' && rawStoredValue.value) {
      storedArray = [rawStoredValue];
    }

    // Filter stored values to only include available options
    const availableValues = availableSources.map(s => s.value);
    const validStoredValues = storedArray.filter(item => 
      availableValues.includes(item.value || item)
    );

    // If no valid values or empty, return default
    if (validStoredValues.length === 0) {
      return [{ label: 'Manufacturing', value: 'manufacturing' }];
    }

    // Return valid stored values
    return validStoredValues;
  });

  useEffect(() => {
    const availableSources = getAvailableVendorSources();
    const availableValues = availableSources.map(s => s.value);
    const onlySourcing = availableSources?.length === 1 && availableSources[0].value === 'sourcing';

    // Normalize stored value to array format
    let storedArray = [];
    if (rawStoredValue) {
      if (Array.isArray(rawStoredValue)) {
        storedArray = rawStoredValue;
      } else if (typeof rawStoredValue === 'object' && rawStoredValue.value) {
        storedArray = [rawStoredValue];
      }
    }

    // Filter stored values to only include available options
    const validStoredValues = storedArray.filter(item => {
      const itemValue = item.value || item;
      return availableValues.includes(itemValue);
    });

    // If profile only has sourcing, force sourcing selection
    if (onlySourcing) {
      const sourcingSelection = [{ label: 'Sourcing', value: 'sourcing' }];
      const currentStored = JSON.parse(sessionStorage.getItem('distributorId')) || {};
      const profileStored = currentStored[selectedProfileId?.profileId];
      
      // Check if stored value is different from sourcing
      const needsUpdate = !profileStored || 
        (Array.isArray(profileStored) && !profileStored.some(item => (item.value || item) === 'sourcing')) ||
        (!Array.isArray(profileStored) && (profileStored.value || profileStored) !== 'sourcing');
      
      if (needsUpdate) {
        sessionStorage.setItem('distributorId', JSON.stringify({ 
          [selectedProfileId?.profileId]: sourcingSelection 
        }));
        setSelected(sourcingSelection);
        return;
      }
    }

    // Migrate old single selection format to array format in sessionStorage
    if (rawStoredValue && !Array.isArray(rawStoredValue)) {
      // Old format: single object - migrate to array format
      const migratedSelection = validStoredValues.length > 0 
        ? validStoredValues 
        : [{ label: 'Manufacturing', value: 'manufacturing' }];
      sessionStorage.setItem('distributorId', JSON.stringify({ [selectedProfileId?.profileId]: migratedSelection }));
      setSelected(migratedSelection);
    } else if (!rawStoredValue || (Array.isArray(rawStoredValue) && rawStoredValue.length === 0)) {
      // No value stored or empty array - set default
      const defaultSelection = [{ label: 'Manufacturing', value: 'manufacturing' }];
      sessionStorage.setItem('distributorId', JSON.stringify({ [selectedProfileId?.profileId]: defaultSelection }));
      setSelected(defaultSelection);
    } else if (validStoredValues.length !== storedArray.length) {
      // Stored values contain invalid options - update with valid ones only
      const correctedSelection = validStoredValues.length > 0 
        ? validStoredValues 
        : [{ label: 'Manufacturing', value: 'manufacturing' }];
      sessionStorage.setItem('distributorId', JSON.stringify({ [selectedProfileId?.profileId]: correctedSelection }));
      setSelected(correctedSelection);
    }
  }, [rawStoredValue, selectedProfileId?.profileId]);

  const [VENDOR_SOURCE] = useState(() => getAvailableVendorSources());

  // Calculate selected count for display
  const selectedCount = selected.length;
  const totalCount = VENDOR_SOURCE.length;
  const displayText = selectedCount > 0 
    ? selected.map(item => item.label).join(', ')
    : '---';

  // if(VENDOR_SOURCE?.length === 1) {
  //   return null;
  // }

  return (
    <div>
      <CustomPopover
        buttonTrigger={
          <div className='d-flex align-items-center justify-content-between Update-Audience-dropdown gap-10'>
            <span className='Update-Audience-dropdown-btn f-w-500 text-black'>
              Vendor Source ({selectedCount}/{totalCount})
            </span>
            {/* - {displayText} */}
            <DownChevron style={{ width: '10px', height: '10px' }} />
          </div>
        }
        popoverContent={
          <VendorSourceList selected={selected} setSelected={setSelected} />
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      />
    </div>
  );
};

const VendorSourceList = ({ selected, setSelected }) => {

  const selectedProfileId = JSON.parse(sessionStorage.getItem('selectedProfileId'));

  const [VENDOR_SOURCE] = useState(() => {

    const distributorView = selectedProfileId?.vendor?.distributorView;

    const sourcingExist = String(distributorView).toLowerCase().includes('sourcing');
    const manufacturingExist = String(distributorView).toLowerCase().includes('manufacturing');

    return [
      ...(sourcingExist ? [{ label: 'Sourcing', value: 'sourcing' }] : []),
      ...(manufacturingExist ? [{ label: 'Manufacturing', value: 'manufacturing' }] : [])
    ]
  });

  const [selectedTempItems, setSelectedTempItems] = useState(selected);

  const location = useLocation()
  const { handleClose = () => { } } = useCustomPopover()
  const [loading, setLoading] = useState(false);

  // Sync temp items when selected changes
  useEffect(() => {
    setSelectedTempItems(selected);
  }, [selected]);
  
  const handleSelection = (item) => {
    setSelectedTempItems(prev => {
      const isSelected = prev.some(selectedItem => selectedItem.value === item.value);
      if (isSelected) {
        // Remove if already selected
        return prev.filter(selectedItem => selectedItem.value !== item.value);
      } else {
        // Add if not selected
        return [...prev, item];
      }
    });
  };

  const handleApply = useCallback(async () => {
    if (selectedTempItems.length === 0) {
      return; // Don't apply if nothing is selected
    }
    setLoading(true);
    setSelected(selectedTempItems);
    sessionStorage.setItem('distributorId', JSON.stringify({ [selectedProfileId?.profileId]: selectedTempItems }));
    window.location.assign(location.pathname)
  }, [selectedTempItems, selectedProfileId?.profileId, location.pathname, setSelected]);

  // Check if selection has changed (compare sorted arrays without mutating)
  const sortedTemp = [...selectedTempItems].sort((a, b) => a.value.localeCompare(b.value));
  const sortedSelected = [...selected].sort((a, b) => a.value.localeCompare(b.value));
  const hasChanged = JSON.stringify(sortedTemp) !== JSON.stringify(sortedSelected);

  return (
    <div className="Update-Audience-dropdown-list" style={{ paddingTop: '8px', paddingBottom: '4px' }}>
      
      {VENDOR_SOURCE.map(item => {
        const isChecked = selectedTempItems.some(selectedItem => selectedItem.value === item.value);
        return (
          <li key={item.value} className='Update-Audience-dropdown-item' style={{ marginBottom: '8px' }}>
            <div className='d-flex align-items-center gap-2 Update-Audience-dropdown-container'>
              <CheckBox
                id={`vendor-source-${item.value}`}
                classes={'Update-Audience-dropdown-checkbox'}
                label={<span className='f-12' style={{ fontWeight: '500 !important' }}>{item?.label}</span>}
                checked={isChecked}
                action={(e) => handleSelection(item)}
              />
            </div>
          </li>
        )
      })}
      <div style={{ borderTop: '1px solid #E8E8E8', marginTop: '8px', marginBottom: '0px' }}></div>
      <div className='update-audience-btn-container'>
        <button
          className='cls-btn cursor-pointer rounded-4 f-10 py-2 px-3 f-w-600 f-202226'
          onClick={() => {
            handleClose()
          }}
        >
          Cancel
        </button>
        <button
          className={`bg-blue ${selectedTempItems.length === 0 || !hasChanged ? 'disabled' : ''} rounded-4 f-10 py-2 px-3 f-w-600 cursor-pointer text-white mt-0 ml-2`}
          onClick={handleApply}
          disabled={selectedTempItems.length === 0 || !hasChanged}
        >
          {loading ? (
            <>Apply <Spinner animation="border" role="status" className="ml-2 wid-15 hei-15 disabled" variant="light" /></>
          ) : (
            'Apply'
          )}
        </button>
      </div>
    </div>
  )
}

export default DropDownDistributor;